/**
 * @typedef {Object} BaseFormData
 * @property {string} email
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} phone
 * @property {string} customerNumberToMatch
 * @property {string} addressOfCompany
 * @property {string} zipCompany
 * @property {string} regionCompany
 * @property {string} chosenCompanyName
 */

/**
 * @typedef {BaseFormData & {
 *   schoolYearId: string,
 *   boxComment: string,
 *   typeOfForm: string,
 * }} RegistrationFormData
 */

/**
 * @typedef {BaseFormData & {
 *   schoolYearId: string,
 *   contactPositionId: string,
 *   comment: string,
 *   schoolVisitPhysical: boolean,
 *   schoolVisitDigital: boolean,
 * }} SchoolVisitData
 */

/**
 * @typedef {BaseFormData & {
 *   projectId: string,
 *   schoolYearId: string,
 *   contactPositionId: string,
 *   comment: string,
 * }} EventRegistrationData
 */

/**
 * @typedef {BaseFormData & {
 *   typeOfBox: string,
 *   schoolYearId: string,
 *   comment: string,
 *   boxComment: string,
 *   typeOfStage: string,
 * }} BoxOrderData
 */

/**
 * @typedef {BaseFormData & {
 *   comment: string,
 *   schoolVisitPhysical: boolean,
 *   schoolVisitDigital: boolean,
 *   lectureChoice: string,
 * }} LectureBookingData
 */

/**
 * @typedef {{ Result: 'true' | 'false' }} DigitalOnlyResult
 */

/**
 * @typedef {{ id: string, name: string }} ContactPosition
 */

const _BASE = "https://majema-free-copy-82691fc887b8a8bad1eb.o2.myshopify.dev";
//NGROK base URL for local development (uncomment when needed):
// const _BASE = "https://8d3e-194-15-225-14.ngrok-free.app";
const _HEADERS = { "ngrok-skip-browser-warning": "true" };
//@ts-ignore
window.SuperOfficeAPI = {
  /**
   * Check whether a zip code is digital-only (no in-person visits).
   * @param {string} zip
   * @returns {Promise<DigitalOnlyResult>}
   */
  async checkIfDigitalOnly(zip) {
    const res = await fetch(
      `${_BASE}/superoffice/check-digital-only?zip=${encodeURIComponent(zip)}`,
      { headers: _HEADERS },
    );
    return res.json();
  },

  /**
   * Submit a newsletter / school subscription registration form.
   * @param {RegistrationFormData} data
   * @returns {Promise<Response>}
   */
  async submitRegistrationForm(data) {
    return fetch(`${_BASE}/superoffice/submit-registration`, {
      method: "POST",
      headers: { ..._HEADERS, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  /**
   * Submit a school visit request (physical or digital).
   * @param {SchoolVisitData} data
   * @returns {Promise<Response>}
   */
  async submitSchoolVisit(data) {
    return fetch(`${_BASE}/superoffice/submit-school-visit`, {
      method: "POST",
      headers: { ..._HEADERS, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  /**
   * Submit a registration for a specific event / träff.
   * @param {EventRegistrationData} data
   * @returns {Promise<Response>}
   */
  async submitEventRegistration(data) {
    return fetch(`${_BASE}/superoffice/submit-event-registration`, {
      method: "POST",
      headers: { ..._HEADERS, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  /**
   * Submit a book box order.
   * @param {BoxOrderData} data
   * @returns {Promise<Response>}
   */
  async submitBoxOrder(data) {
    return fetch(`${_BASE}/superoffice/submit-box-order`, {
      method: "POST",
      headers: { ..._HEADERS, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  /**
   * Submit a lecture / workshop booking.
   * @param {LectureBookingData} data
   * @returns {Promise<Response>}
   */
  async submitLectureBooking(data) {
    return fetch(`${_BASE}/superoffice/submit-lecture-booking`, {
      method: "POST",
      headers: { ..._HEADERS, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  /**
   * Fetch the list of contact positions for the position dropdown.
   * @returns {Promise<{ contactPositions: ContactPosition[] }>}
   */
  async getContactPositions() {
    const res = await fetch(`${_BASE}/superoffice/contact-positions`, {
      headers: _HEADERS,
    });
    return res.json();
  },

  /**
   * Fetch published events filtered by type (used in evenemang form).
   * @param {string} typeId
   * @returns {Promise<{ events: Array<{ id: string, name: string }> }>}
   */
  async getPublishedEvents(typeId) {
    const res = await fetch(
      `${_BASE}/superoffice/published-events?typeId=${encodeURIComponent(typeId)}`,
      { headers: _HEADERS },
    );
    return res.json();
  },
};
